import { useMemo, useCallback, Fragment } from "react"
import { Avatar, Box, Typography, useTheme } from "@mui/material"
import { Link } from "react-router-dom"
import _ from "lodash"

import { SKELETON_COUNT, SKELETON_INDEX_TO_REF } from "constant"
import { getIcon } from "utils/icon"
import { getFeatureDetail, IOccupantDetail } from "utils/feature"

import { useTranslation } from "providers/i18n"
import { useVenue } from "providers/venue"
import { useDining } from "hooks/dining/useDining"

import { AppNavigation as DefaultAppNavigation } from "containers/kiosk-h"
import { PageCard, PageCardContent } from "components/card"
import { VenueSelector } from "components/VenueSelector"
import { HorizontalScrollPane } from "components/HorizontalScrollPane"
import {
  CategoryFilterBar,
  CategoryFilterChip,
} from "components/CategoryFilterBar"
import { MotionDiv } from "components/motions"
import { EmptyData } from "components/EmptyData"
import { DiningIcon } from "components/icons"
import { ImageCard, ImageSkeletonCard } from "components/ImageCard"
import { IFeature } from "interfaces"
import { ContentWithAside } from "layouts/kiosk-h"
import { SidebarMenu } from "components/navigation/SidebarMenu"

export const DiningKioskHPage = ({
  Layout = ContentWithAside,
  AppNavigation = DefaultAppNavigation,
}) => {
  const { isMultiOrdinalVenue } = useVenue()
  const {
    restaurants,
    currentCategory,
    categories,
    isMultiVenueProject,
    locationVenueId,
    venues,
    venueFilter,
    hasData,
    isFetched,
    hasNextPage,
    endScrollRef,
    filterByCategoryId,
    filterByVenueId,
  } = useDining()
  const theme = useTheme()
  const { t } = useTranslation()

  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.dining) || <DiningIcon />,
    [theme]
  )
  const handleChangeCategory = useCallback(
    (e, id) => {
      if (id === null) return
      filterByCategoryId(id)
    },
    [filterByCategoryId]
  )

  const showSubCategoryFilter = categories.length > 0
  const { styleOverrides = {} } = theme.components?.[`AppDining`] || {}

  const {
    pageCard,
    contentWrapper,
    gridContainer,
    gridItem,
    venueSelector,
    filterSection,
    scrollPane,
    emptyDataWrapper,
    diningAvatarFallBack,
    diningImageCard,
    diningAvatar,
    diningFallbackImageIconWrapper,
    diningFallbackImageIcon,
    subCategoryFilterBar,
  } = styleOverrides

  return (
    //@ts-ignore
    <Layout
      aside={
        <PageCard>
          <SidebarMenu AppNavigation={AppNavigation} />
        </PageCard>
      }
    >
      <PageCard sx={pageCard}>
        <PageCardContent>
          <MotionDiv
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            sx={contentWrapper}
          >
            <Box
              sx={gridContainer}
              className={showSubCategoryFilter || hasData ? "" : "emptyList"}
            >
              {hasData ? (
                <>
                  {restaurants.pages.map((page, pageNum) => {
                    return (
                      <Fragment key={pageNum}>
                        {page.data.map((restaurant, index) => {
                          const {
                            group,
                            level,
                            logo,
                            featuredImage,
                            name,
                            isFeatured,
                            boothName,
                            categoryIcon,
                            subCategory,
                          } =
                            (getFeatureDetail(
                              restaurant as IFeature
                            ) as IOccupantDetail) || {}

                          const levelText = isMultiOrdinalVenue
                            ? t("Level Text", { level: t(level) })
                            : ""
                          const groupText = t(group)
                          return (
                            <ImageCard
                              variant="full-image"
                              key={restaurant?.id}
                              sx={diningImageCard}
                              component={Link}
                              to={`/maps/place/${restaurant?.id}`}
                              title={t(name)}
                              subheader={
                                <>
                                  <Typography
                                    component="span"
                                    className="sub-category-text"
                                  >
                                    {t(subCategory)}
                                  </Typography>
                                  {boothName && (
                                    <Typography
                                      component="span"
                                      className="booth-name-text"
                                    >
                                      {`${t("Booth")} ${t(boothName)}`}
                                    </Typography>
                                  )}
                                  <Typography
                                    className="location-text"
                                    component="span"
                                  >
                                    {_.compact([levelText, groupText]).join(
                                      " "
                                    )}
                                  </Typography>
                                </>
                              }
                              image={featuredImage}
                              fallbackImageIcon={
                                <Box sx={diningFallbackImageIconWrapper}>
                                  {getIcon(
                                    categoryIcon,
                                    diningFallbackImageIcon,
                                    {
                                      imageIconProps: {
                                        solidColorFilterId:
                                          "diningCard_cover_filter",
                                      },
                                    }
                                  )}
                                </Box>
                              }
                              avatar={
                                <Avatar src={logo} sx={diningAvatar}>
                                  {getIcon(categoryIcon, diningAvatarFallBack, {
                                    imageIconProps: {
                                      solidColorFilterId:
                                        "diningCard_avatar_filter",
                                    },
                                  })}
                                </Avatar>
                              }
                              tag={isFeatured ? t("Highlight") : null}
                            />
                          )
                        })}
                      </Fragment>
                    )
                  })}
                  {hasNextPage &&
                    Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                      <ImageSkeletonCard
                        ref={
                          index === SKELETON_INDEX_TO_REF ? endScrollRef : null
                        }
                        key={index}
                        sx={gridItem}
                      />
                    ))}
                </>
              ) : !isFetched ? (
                Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                  <ImageSkeletonCard key={index} sx={gridItem} />
                ))
              ) : (
                <Box sx={emptyDataWrapper}>
                  <EmptyData
                    icon={emptyIcon}
                    primary={t("Coming soon")}
                    secondary={t("Stay tuned for the update")}
                  />
                </Box>
              )}
            </Box>
            {isMultiVenueProject && (
              <Box sx={venueSelector}>
                <VenueSelector
                  selectedVenue={venueFilter}
                  venues={venues}
                  onClickVenue={filterByVenueId}
                  currentVenue={locationVenueId}
                />
              </Box>
            )}
            {showSubCategoryFilter && (
              <Box sx={filterSection}>
                <HorizontalScrollPane
                  stackProps={{
                    sx: scrollPane,
                  }}
                >
                  <CategoryFilterBar
                    sx={subCategoryFilterBar}
                    activeCategory={currentCategory}
                    categories={categories}
                    renderFilterButton={CategoryFilterChip}
                    onChange={handleChangeCategory}
                    enableSeparateBorder={false}
                  />
                </HorizontalScrollPane>
              </Box>
            )}
          </MotionDiv>
        </PageCardContent>
      </PageCard>
    </Layout>
  )
}
